import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import CreatorStudioLayout from "./CreatorStudioLayout";
import AudienceInsights from "../../components/creator/AudienceInsights";
import { computeAnalyticsFromDrops, fetchCreatorDrops } from "../../services/creatorStudioService";
import { contentTypeLabel, dropViewCount, formatCount } from "./creatorHelpers";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function buildHeatmap(drops) {
  const grid = DAYS.map(() => HOURS.map(() => 0));
  drops.forEach((drop) => {
    const date = drop.createdAt?.toDate?.();
    if (!date) return;
    const dayIdx = (date.getDay() + 6) % 7;
    const hour = date.getHours();
    grid[dayIdx][hour] += Number(drop.viewCount || 0) + 1;
  });
  const max = Math.max(...grid.flat(), 1);
  return grid.map((row) =>
    row.map((v) => {
      if (v === 0) return 0;
      const ratio = v / max;
      if (ratio < 0.25) return 1;
      if (ratio < 0.5) return 2;
      if (ratio < 0.75) return 3;
      return 4;
    })
  );
}

const PROVINCES = [
  { name: "Ontario", pct: 38 },
  { name: "British Columbia", pct: 22 },
  { name: "Alberta", pct: 14 },
  { name: "Quebec", pct: 12 },
  { name: "Other", pct: 14 },
];

const SOURCES = [
  { label: "Gallery", pct: 42, color: "var(--gold)" },
  { label: "Home feed", pct: 28, color: "var(--niche)" },
  { label: "Profile", pct: 18, color: "var(--text-mid)" },
  { label: "Search", pct: 12, color: "var(--text-muted)" },
];

export default function Analytics() {
  const { currentUser, userProfile } = useAuth();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30 days");

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    fetchCreatorDrops(currentUser.uid)
      .then(setDrops)
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const analytics = useMemo(() => computeAnalyticsFromDrops(drops), [drops]);
  const heatmap = useMemo(() => buildHeatmap(drops), [drops]);
  const totalViews = analytics.totalViews;
  const openDrops = drops.filter((d) => d.status === "open" || dropViewCount(d) < 100);

  const bestHour = useMemo(() => {
    const flat = heatmap.flat();
    const maxLevel = Math.max(...flat);
    if (maxLevel === 0) return "7–9 PM";
    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 24; h++) {
        if (heatmap[d][h] === maxLevel) {
          const start = h % 12 || 12;
          const ampm = h < 12 ? "AM" : "PM";
          return `${start} ${ampm}`;
        }
      }
    }
    return "7–9 PM";
  }, [heatmap]);

  if (loading) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading analytics…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout>
      <div className="cs-page-eyebrow">Creator Studio</div>
      <h1 className="cs-page-title">Your <em>Analytics</em></h1>
      <p className="cs-page-sub">Understand how fans find, unlock, and engage with your drops.</p>

      <div className="cs-period-tabs">
        {["7 days", "30 days", "90 days", "All time"].map((r) => (
          <button key={r} type="button" className={`cs-period-tab${dateRange === r ? " active" : ""}`} onClick={() => setDateRange(r)}>
            {r}
          </button>
        ))}
      </div>

      <div className="cs-insight-banner">
        <div className="cs-insight-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth="2" strokeLinecap="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="cs-insight-text">
          <strong>
            {openDrops.length
              ? `${openDrops.length} drop${openDrops.length > 1 ? "s" : ""} currently open — ${totalViews} total views`
              : "Post a drop to start collecting analytics"}
          </strong>
          <p>Your best posting window appears to be around {bestHour} based on view activity.</p>
          <span className="cs-insight-hand">data updates in real time as views come in</span>
        </div>
      </div>

      <div className="cs-stats-row">
        <div className="cs-stat-card">
          <div className="cs-stat-num">{formatCount(totalViews)}</div>
          <div className="cs-stat-label">Total views</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num">{analytics.avgViewsPerDrop}</div>
          <div className="cs-stat-label">Avg views per drop</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num">{analytics.saveRate}%</div>
          <div className="cs-stat-label">Save rate</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num">{userProfile?.followerCount || 0}</div>
          <div className="cs-stat-label">Followers</div>
        </div>
      </div>

      <div className="cs-grid-21">
        <div className="cs-card">
          <div className="cs-card-title">Activity heatmap</div>
          <div className="cs-card-sub">When fans view your drops — darker = more activity</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 3, marginBottom: 4, paddingLeft: 30 }}>
            {[0, 6, 12, 18, 23].map((h) => (
              <span key={h} style={{ fontSize: 8, color: "var(--text-muted)", gridColumn: h === 0 ? 1 : undefined }}>{h}</span>
            ))}
          </div>
          {DAYS.map((day, di) => (
            <div key={day} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: "var(--text-muted)", width: 24, flexShrink: 0 }}>{day}</span>
              <div className="cs-heatmap" style={{ flex: 1 }}>
                {heatmap[di].map((level, hi) => (
                  <div key={hi} className={`cs-heat-cell${level ? ` cs-heat-${level}` : ""}`} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="cs-card">
          <div className="cs-card-title">Traffic sources</div>
          <div className="cs-card-sub">Where fans discover your drops</div>
          {SOURCES.map((src) => (
            <div key={src.label} className="cs-source-row">
              <span className="cs-source-label">{src.label}</span>
              <div className="cs-source-bar-wrap">
                <div className="cs-source-bar" style={{ width: `${src.pct}%`, background: src.color }} />
              </div>
              <span className="cs-source-pct">{src.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-sec-header">
        <div className="cs-sec-eyebrow">Audience Intelligence</div>
        <div className="cs-sec-title">Who your fans are and where they&apos;re from</div>
      </div>

      <div className="cs-grid-2">
        <div className="cs-card">
          <div className="cs-card-title">Followers by province</div>
          <div className="cs-card-sub">Where your Canadian fans are located</div>
          {PROVINCES.map((p) => (
            <div key={p.name} className="cs-province-row">
              <span className="cs-province-name">{p.name}</span>
              <div className="cs-source-bar-wrap" style={{ height: 8 }}>
                <div className="cs-province-bar" style={{ width: `${p.pct}%` }} />
              </div>
              <span className="cs-source-pct">{p.pct}%</span>
            </div>
          ))}
        </div>

        <AudienceInsights />
      </div>

      <div className="cs-sec-header">
        <div className="cs-sec-eyebrow">Content Performance</div>
        <div className="cs-sec-title">What works best for your audience</div>
      </div>

      <div className="cs-grid-2" style={{ marginBottom: 20 }}>
        <div className="cs-card">
          <div className="cs-card-title">Content type breakdown</div>
          <div className="cs-card-sub">Performance by format</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {(analytics.contentTypes.length ? analytics.contentTypes : [{ type: "video", count: 0, avgViews: 0 }]).map((ct) => (
              <div key={ct.type} className="cs-ct-card">
                <div className="cs-drop-meta" style={{ fontWeight: 700, marginBottom: 6 }}>{contentTypeLabel(ct.type)}</div>
                <div className="cs-ct-stat">{ct.count} <em style={{ fontSize: 16 }}>drops</em></div>
                <div className="cs-earn-sub">{ct.avgViews} avg views</div>
              </div>
            ))}
          </div>
        </div>

        <div className="cs-card">
          <div className="cs-card-title">Price point analysis</div>
          <div className="cs-card-sub">Conversion by unlock price</div>
          {analytics.pricePoints.length === 0 ? (
            <div className="cs-empty" style={{ padding: 20 }}>No paid drops yet.</div>
          ) : (
            analytics.pricePoints.map((pp) => (
              <div key={pp.price} className="cs-source-row" style={{ marginBottom: 12 }}>
                <span className="cs-source-label" style={{ width: 60, fontWeight: 700 }}>{pp.price}</span>
                <div className="cs-source-bar-wrap" style={{ height: 8 }}>
                  <div className="cs-source-bar" style={{ width: `${Math.min(pp.count * 25, 100)}%` }} />
                </div>
                <span className="cs-source-pct">{pp.count}</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)", width: 80, textAlign: "right" }}>{pp.count} drop{pp.count > 1 ? "s" : ""}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="cs-card">
        <div className="cs-card-title">Drop performance</div>
        <div className="cs-card-sub">How fast drops fill their 100 spots</div>
        {analytics.performance.length === 0 ? (
          <div className="cs-empty">No published drops yet.</div>
        ) : (
          analytics.performance.slice(0, 8).map((drop) => {
            const speed = drop.fillRate >= 80 ? "fast" : drop.fillRate >= 40 ? "med" : "slow";
            const speedLabel = speed === "fast" ? `${drop.views}/100 — closing` : speed === "med" ? `${drop.views}/100 views` : `${drop.views}/100 — slow fill`;
            return (
              <div key={drop.id} className="cs-perf-row">
                {drop.thumbnailUrl ? (
                  <img className="cs-perf-thumb" src={drop.thumbnailUrl} alt="" />
                ) : (
                  <div className="cs-perf-thumb" style={{ background: "var(--bg)" }} />
                )}
                <div className="cs-perf-info">
                  <div className="cs-perf-title">{drop.title || "Untitled"}</div>
                  <div className="cs-perf-meta">{contentTypeLabel(drop.contentType)}</div>
                </div>
                <div className={`cs-perf-speed ${speed}`}>{speedLabel}</div>
              </div>
            );
          })
        )}
      </div>
    </CreatorStudioLayout>
  );
}
